import React, { useState, useEffect, useRef } from 'react';
import { useDesign } from '../../designs';

interface TravelTimeInputProps {
  label: string;
  value: string; // "HH:MM" 24-hour
  onChange: (value: string) => void;
}

// 24h "HH:MM" → { digits: "947", ampm: "AM" }
function parse24ToDigits(time24: string): { digits: string; ampm: 'AM' | 'PM' } {
  if (!time24) return { digits: '', ampm: 'AM' };
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return { digits: `${h}${m.toString().padStart(2, '0')}`, ampm };
}

// Format displayed input from raw digits: "247" → "2:47", "1130" → "11:30"
function formatDigits(digits: string): string {
  if (!digits) return '';
  if (digits.length <= 2) return digits;
  if (digits.length === 3) return `${digits[0]}:${digits.slice(1)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
}

// Digits + ampm → 24h "HH:MM" (or '' if invalid/incomplete)
function digitsTo24(digits: string, ampm: 'AM' | 'PM'): string {
  if (!digits || digits.length < 3) return '';
  let h12: number;
  let min: number;
  if (digits.length === 3) {
    h12 = parseInt(digits[0], 10);
    min = parseInt(digits.slice(1), 10);
  } else {
    h12 = parseInt(digits.slice(0, 2), 10);
    min = parseInt(digits.slice(2, 4), 10);
  }
  if (isNaN(h12) || isNaN(min)) return '';
  if (h12 < 1 || h12 > 12) return '';
  if (min < 0 || min > 59) return '';
  let h24 = h12 % 12;
  if (ampm === 'PM') h24 += 12;
  return `${h24.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}

export default function TravelTimeInput({ label, value, onChange }: TravelTimeInputProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const initial = parse24ToDigits(value);
  const [digits, setDigits] = useState(initial.digits);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(initial.ampm);
  const [error, setError] = useState('');
  const valueRef = useRef(value);

  // Sync from external value changes (e.g., loading an existing entry)
  useEffect(() => {
    if (value !== valueRef.current) {
      valueRef.current = value;
      const p = parse24ToDigits(value);
      setDigits(p.digits);
      setAmpm(p.ampm);
    }
  }, [value]);

  const pushChange = (newDigits: string, newAmpm: 'AM' | 'PM') => {
    if (!newDigits) {
      valueRef.current = '';
      onChange('');
      setError('');
      return;
    }
    const result = digitsTo24(newDigits, newAmpm);
    if (newDigits.length >= 3 && !result) {
      setError('Invalid time');
      return;
    }
    setError('');
    if (result) {
      valueRef.current = result;
      onChange(result);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
    setDigits(raw);
    pushChange(raw, ampm);
  };

  const setAmpmAndPush = (next: 'AM' | 'PM') => {
    setAmpm(next);
    pushChange(digits, next);
  };

  const displayed = formatDigits(digits);

  const ampmButton = (which: 'AM' | 'PM') => {
    const active = ampm === which;
    return (
      <button
        type="button"
        onClick={() => setAmpmAndPush(which)}
        style={{
          padding: '0 10px', minHeight: '41px', minWidth: '44px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 700,
          letterSpacing: '1.5px',
          color: active ? '#EDE4D8' : d.textSecondary,
          backgroundColor: active ? '#2A9068' : 'transparent',
          border: `1px solid ${active ? '#2A9068' : 'rgba(200,146,58,0.20)'}`,
          borderRadius: '2px', cursor: 'pointer',
          WebkitAppearance: 'none', appearance: 'none',
        }}
      >{which}</button>
    );
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label style={{
        display: 'block', fontFamily: "'Raleway', sans-serif",
        fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px',
        textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
      }}>{label}</label>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={displayed}
          onChange={handleInputChange}
          placeholder="0:00"
          onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
          style={{
            flex: 1, minWidth: 0,
            padding: '10px 12px', minHeight: '41px',
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
            color: d.textPrimary, backgroundColor: d.bgSurface,
            border: '1px solid rgba(200,146,58,0.10)',
            borderRadius: '2px', outline: 'none', boxSizing: 'border-box',
            WebkitAppearance: 'none', appearance: 'none',
          }}
        />
        {ampmButton('AM')}
        {ampmButton('PM')}
      </div>
      {error && (
        <p style={{
          margin: '4px 0 0',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.error,
        }}>{error}</p>
      )}
    </div>
  );
}
